import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, IconButton, useMediaQuery } from "@material-ui/core";
import { useMutation, useQuery } from "@apollo/react-hooks";
import CloseIcon from "@material-ui/icons/Close";

import { ReactComponent as Warning } from "assets/warning-red.svg";
import {
  addBackgroundColorToRoot,
  breakpoints,
  color,
  fontFamily,
  fontSize,
  removeBackgroundColorFromRoot,
  spacingDefaults
} from "style/constants";
import {
  Button,
  ConfirmationModal,
  Loading,
  RadioCard,
  StyledGrid
} from "components/simple";
import {
  GetResourceSurvey,
  MarkResourceAsViewed,
  SaveResourceSurveyAnswers
} from "graphql/userResources/userResources.gql";
import {
  ResourceSurveyData,
  ResourceSurvey as ResourceSurveyType
} from "models/userResource";

import { SurveyAnswers } from "graphql/types/Contentful";
import { showErrorNotification } from "state";
import { useDispatch } from "react-redux";
import ResourceSurveyProgressBar from "./ResourceSurveyProgressBar";
import { GetSubHeaderData, Me } from "graphql/user/user.gql";

const ResourceSurvey: React.FC = () => {
  const mobile = useMediaQuery(`(max-width:${breakpoints.lg}px)`);
  const history = useHistory();
  const dispatch = useDispatch();

  const { state } = useLocation<{
    contentfulResourceId: string;
    resourceTitle: string;
  }>();

  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const [questionIndex, setQuestionIndex] = useState<number>(0);
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswers[]>([]);
  const [resourceSurvey, setResourceSurvey] = useState<ResourceSurveyType>();
  const [selectedAnswer, setSelectedAnswer] = useState<SurveyAnswers>({
    questionId: "",
    answer: ""
  });
  const hasSelectedAnswer = selectedAnswer?.answer !== "";

  const { loading } = useQuery<ResourceSurveyData>(GetResourceSurvey, {
    onCompleted: data => setResourceSurvey(data?.resourceSurvey)
  });

  const [saveSurveyAnswers] = useMutation(SaveResourceSurveyAnswers, {
    onCompleted: () => {
      setQuestionIndex(0);
      setSurveyAnswers([]);
    }
  });
  const [markResourceAsViewed] = useMutation(MarkResourceAsViewed, {
    refetchQueries: [{ query: GetSubHeaderData }]
  });

  const nextQuestion = (selectedAnswer: SurveyAnswers) => {
    setQuestionIndex(questionIndex + 1);
    setSurveyAnswers([
      ...surveyAnswers,
      { questionId: selectedAnswer.questionId, answer: selectedAnswer.answer }
    ]);
  };

  const completeSurvey = (selectedAnswer: SurveyAnswers) => {
    setSurveyAnswers([
      ...surveyAnswers,
      { questionId: selectedAnswer.questionId, answer: selectedAnswer.answer }
    ]);
  };

  useEffect(() => {
    if (
      resourceSurvey &&
      surveyAnswers.length === resourceSurvey?.questions.length
    )
      saveAndCompleteSurvey();
  }, [surveyAnswers]);

  const saveAndCompleteSurvey = async () => {
    try {
      await saveSurveyAnswers({
        variables: {
          input: {
            contentfulResourceId: state.contentfulResourceId,
            resourceTitle: state.resourceTitle,
            surveyAnswers
          }
        }
      });
    } catch (e) {
      dispatch(showErrorNotification("Error", e));
    }

    try {
      const { data } = await markResourceAsViewed({
        variables: {
          input: {
            contentfulResourceId: state.contentfulResourceId,
            name: state.resourceTitle
          }
        }
      });
      history.push("/resource-survey/complete", {
        coinsAwarded: data?.markUserResourceComplete?.coinsAwarded,
        contentfulResourceId: state.contentfulResourceId,
        resourceTitle: state.resourceTitle
      });
    } catch (e) {
      dispatch(showErrorNotification("Error", e));
    }
  };

  addBackgroundColorToRoot(color.WHITE);
  useEffect(() => {
    return () => {
      removeBackgroundColorFromRoot();
    };
  }, []);

  useEffect(() => {
    setSelectedAnswer({ questionId: currentQuestion?.id || "", answer: "" });
  }, [questionIndex]);

  const currentQuestion = resourceSurvey?.questions[questionIndex];

  if (loading) return <Loading />;

  return (
    <>
      <ResourceSurveyProgressBar
        numberCompleted={questionIndex}
        numberOfQuestions={resourceSurvey?.questions?.length || 0}
      />
      <StyledGrid position="absolute" margin={spacingDefaults.normal}>
        <IconButton size="medium" onClick={() => setShowExitModal(true)}>
          <CloseIcon />
        </IconButton>
      </StyledGrid>
      <StyledGrid container centerContent>
        <StyledGrid
          item
          style={{
            maxWidth: "500px",
            padding: `0 ${spacingDefaults.normal}`
          }}
        >
          <StyledGrid item>
            <Box
              fontFamily={fontFamily.secondary}
              fontSize={fontSize.large}
              margin={`${spacingDefaults.normal} 0`}
            >
              {currentQuestion?.question}
            </Box>
            <RadioCard
              column
              icons={
                currentQuestion?.possibleAnswers?.map(
                  item => item?.iconImage || ""
                ) || []
              }
              selections={
                currentQuestion?.possibleAnswers?.map(item => item.answer) || []
              }
              onChange={(_, value) => {
                setSelectedAnswer({
                  questionId: currentQuestion?.id || "",
                  answer: value
                });
              }}
              value={selectedAnswer.answer}
            />
          </StyledGrid>
          <StyledGrid item margin={`${spacingDefaults.medium} 0`}>
            <Button
              stickyFooter={mobile ? true : false}
              borderRadius={mobile ? "0px" : undefined}
              zIndex={2}
              disabled={!hasSelectedAnswer}
              width={mobile ? undefined : "200px"}
              onClick={() => {
                if (resourceSurvey) {
                  questionIndex === resourceSurvey?.questions.length - 1
                    ? completeSurvey(selectedAnswer)
                    : nextQuestion(selectedAnswer);
                }
              }}
            >
              {resourceSurvey &&
              questionIndex === resourceSurvey?.questions.length - 1
                ? "Complete"
                : "Next"}
            </Button>
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>
      {showExitModal && (
        <ConfirmationModal
          icon={<Warning width={"150px"} height={"150px"} />}
          title={"Are you sure you want to exit?"}
          content={`You will lose all of your progress if you exit now.`}
          confirmText={"Yes, exit survey"}
          onConfirm={() => {
            history.push("/resources");
          }}
          cancelText={"Oh, wait. Never mind"}
          onCancel={() => setShowExitModal(false)}
        />
      )}
    </>
  );
};
export default ResourceSurvey;
